import React, { useState, useEffect } from 'react';
import {
  X,
  CheckCircle2,
  MessageCircle,
  Sparkles,
  Trash2,
  Calendar,
  Clock,
  MapPin,
  ShoppingBag,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Smartphone,
  Tag,
  Check,
  Plus,
  Truck,
  AlertCircle,
  Navigation,
  Compass,
  ExternalLink,
  Star
} from 'lucide-react';
import { CONTACT_INFO, formatDuration, calculateEndTime } from '../data/services';
import { BookingRecord, getStoredBookings } from '../utils/excelExport';
import { createBookingOrder, getCustomerBookings } from '../services/bookingsService';
import { submitReview } from '../services/reviewService';
import {
  detectLiveGeolocation,
  makeGoogleMapsUrl,
  loadSavedUserLocation,
  saveUserLocation,
  ALLOWED_CART_AREAS,
  getAllowedCartAreas,
  OPERATIONAL_AREAS_CHANGED_EVENT,
  saveCustomerAddress,
  getSavedCustomerAddresses,
  findClosestLocality
} from '../services/locationService';
import {
  TIME_SLOTS,
  getSlotAvailability,
  selectRoundRobinBeautician,
  SlotAvailabilityInfo,
  getLocalTodayDateString,
  getLocalTomorrowDateString,
  isSlotAvailableTimeWise,
  getVisibleSlotsForDate,
  getNextSlot,
  isLongServiceOrder
} from '../services/slotService';

export interface SelectedServiceDetails {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  category?: string;
  durationMinutes?: number;
}

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: SelectedServiceDetails[];
  onRemoveItem: (id: string) => void;
  onAddItem?: (item: SelectedServiceDetails) => void;
  onClearCart: () => void;
  isPageView?: boolean;
}

export interface SmartCouponTier {
  minAmount: number;
  discount: number;
  label: string;
}

export const SMART_COUPON_TIERS: SmartCouponTier[] = [
  { minAmount: 2150, discount: 250, label: '₹2,150+ → ₹250 OFF' },
  { minAmount: 1550, discount: 200, label: '₹1,550+ → ₹200 OFF' },
  { minAmount: 1250, discount: 150, label: '₹1,250+ → ₹150 OFF' },
  { minAmount: 850, discount: 100, label: '₹850+ → ₹100 OFF' },
  { minAmount: 500, discount: 50, label: '₹500+ → ₹50 OFF' },
];

export const calculateSmartCouponDiscount = (price: number): number => {
  for (const tier of SMART_COUPON_TIERS) {
    if (price >= tier.minAmount) {
      return tier.discount;
    }
  }
  return 0;
};

export const getNextSmartCouponTier = (price: number): { needMore: number; nextDiscount: number } | null => {
  if (price < 500) return { needMore: 500 - price, nextDiscount: 50 };
  if (price < 850) return { needMore: 850 - price, nextDiscount: 100 };
  if (price < 1250) return { needMore: 1250 - price, nextDiscount: 150 };
  if (price < 1550) return { needMore: 1550 - price, nextDiscount: 200 };
  if (price < 2150) return { needMore: 2150 - price, nextDiscount: 250 };
  return null;
};

interface Coupon {
  code: string;
  label: string;
  description: string;
  type: 'flat' | 'smart_tiered';
  value: number;
  minOrder: number;
  firstBookingOnly?: boolean;
}

const AVAILABLE_COUPONS: Coupon[] = [
  {
    code: 'FIRSTGLOW',
    label: 'Up to ₹250 OFF',
    description: 'First Booking Special: Save ₹50 on ₹500, ₹100 on ₹850, ₹150 on ₹1250, ₹200 on ₹1550, ₹250 on ₹2150+',
    type: 'smart_tiered',
    value: 250,
    minOrder: 500,
    firstBookingOnly: true
  }
];

export const ALL_DROPDOWN_AREAS: string[] = [
  ...ALLOWED_CART_AREAS,
  'Others (Coming Soon)'
];

export interface RecommendedBudgetAddon {
  id: string;
  name: string;
  displayName?: string;
  price: number;
  originalPrice: number;
  durationMinutes: number;
  category: string;
  badge: string;
  description: string;
}

export const RECOMMENDED_BUDGET_ADDONS: RecommendedBudgetAddon[] = [
  {
    id: 'hair-spa',
    name: 'Loreal Hair Spa',
    price: 599,
    originalPrice: 899,
    durationMinutes: 45,
    category: 'hairspa',
    badge: '💆‍♀️ Hair Spa',
    description: 'Cream bath, steam & relaxing scalp massage'
  },
  {
    id: 'srv-1790614375810-v4vm',
    name: 'Head Massage',
    price: 499,
    originalPrice: 799,
    durationMinutes: 30,
    category: 'relaxing-massage',
    badge: '✨ Head Massage',
    description: '30 mins stress-relief head massage'
  },
  {
    id: 'cf-basic-facial',
    name: 'Aroma Fruit Facial',
    displayName: 'Insta Glow Fruit Facial',
    price: 599,
    originalPrice: 849,
    durationMinutes: 45,
    category: 'facial',
    badge: '🌟 Insta Glow',
    description: 'Hydrating daily glow & fruit massage'
  },
  {
    id: 'cf-glow-cleanup',
    name: 'Glow Cleanup',
    price: 449,
    originalPrice: 649,
    durationMinutes: 35,
    category: 'cleanup',
    badge: '🌿 Glow De-Tan',
    description: 'Instant skin brightness & gentle scrub'
  },
  {
    id: 'srv-1790615271456-ym8l',
    name: 'Face Bleach',
    displayName: 'Face Golden Glow Bleach',
    price: 199,
    originalPrice: 399,
    durationMinutes: 20,
    category: 'cleanup',
    badge: '⚡ Golden Radiance',
    description: 'Instant radiant brightness & golden sheen'
  }
];

export const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  selectedItems,
  onRemoveItem,
  onAddItem,
  onClearCart,
  isPageView = false
}) => {
  // Step State (1: Cart & Coupon, 2: Slot & Address)
  const [step, setStep] = useState<1 | 2>(1);

  // Quick-Add / Recommended Addon state
  const isRecommendedInCart = (id: string) => selectedItems.some((item) => item.id === id);

  const handleToggleRecommended = (addon: RecommendedBudgetAddon) => {
    if (isRecommendedInCart(addon.id)) {
      onRemoveItem(addon.id);
    } else if (onAddItem) {
      onAddItem({
        id: addon.id,
        name: addon.name,
        price: addon.price,
        originalPrice: addon.originalPrice,
        category: addon.category,
        durationMinutes: addon.durationMinutes
      });
    }
  };

  const renderPeopleAlsoLike = () => (
    <div className="cart-recommendations-section">
      <div className="cart-recommendations-header">
        <div className="cart-recom-title-wrap">
          <div className="cart-recom-icon-badge">
            <Sparkles size={14} />
          </div>
          <div>
            <h5 className="cart-recom-heading">People Also Like</h5>
            <p className="cart-recom-sub">Popular low-budget doorstep additions</p>
          </div>
        </div>
        <span className="cart-recom-badge-tag">From ₹199</span>
      </div>

      <div className="cart-recom-scroll-row">
        {RECOMMENDED_BUDGET_ADDONS.map((addon) => {
          const inCart = isRecommendedInCart(addon.id);
          const savings = addon.originalPrice - addon.price;
          return (
            <div
              key={addon.id}
              className={`cart-recom-card ${inCart ? 'is-in-cart' : ''}`}
            >
              <div className="cart-recom-top-meta">
                <span className="cart-recom-chip">{addon.badge}</span>
                <span className="cart-recom-time">
                  <Clock size={10} style={{ marginRight: '2px', verticalAlign: 'middle' }} />
                  {addon.durationMinutes}m
                </span>
              </div>

              <h6 className="cart-recom-name">{addon.displayName || addon.name}</h6>
              <p className="cart-recom-desc">{addon.description}</p>

              <div className="cart-recom-bottom-row">
                <div className="cart-recom-pricing">
                  <div className="cart-recom-price-line">
                    <span className="cart-recom-orig">₹{addon.originalPrice}</span>
                    <span className="cart-recom-price">₹{addon.price}</span>
                  </div>
                  {savings > 0 && (
                    <span className="cart-recom-save">Save ₹{savings}</span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handleToggleRecommended(addon)}
                  className={`cart-recom-btn ${inCart ? 'is-added' : ''}`}
                  title={inCart ? `Remove ${addon.name} from cart` : `Add ${addon.name} to cart`}
                  aria-label={inCart ? `Remove ${addon.name} from cart` : `Add ${addon.name} to cart`}
                >
                  {inCart ? (
                    <>
                      <Check size={12} />
                      <span>Added</span>
                    </>
                  ) : (
                    <>
                      <Plus size={12} />
                      <span>Add</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Coupon State
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number } | null>(null);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  // Customer & Slot form fields
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [allowedAreas, setAllowedAreas] = useState<string[]>(() => getAllowedCartAreas());
  const [dropdownAreas, setDropdownAreas] = useState<string[]>(() => [...getAllowedCartAreas(), 'Others (Coming Soon)']);

  const [area, setArea] = useState(() => {
    const saved = loadSavedUserLocation();
    const currentAllowed = getAllowedCartAreas();
    if (saved?.areaName && (currentAllowed.includes(saved.areaName) || saved.areaName.toLowerCase().includes('other'))) {
      return saved.areaName;
    }
    return currentAllowed[0] || 'Power House';
  });
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [preferredDate, setPreferredDate] = useState(() => {
    return getLocalTomorrowDateString();
  });
  const [selectedSlot, setSelectedSlot] = useState(() => {
    const tomorrow = getLocalTomorrowDateString();
    const visible = getVisibleSlotsForDate(tomorrow);
    return visible[0] || TIME_SLOTS[0];
  });
  const [specialRequests, setSpecialRequests] = useState('');
  const [slotsInfo, setSlotsInfo] = useState<Record<string, SlotAvailabilityInfo>>({});
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotError, setSlotError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hyperlocal Location & Google Maps States
  // In checkout cart, never prefill exact Google Maps location or coordinates even if an area was chosen at start.
  // The customer has the free choice to either tap "Pin Live GPS" or paste their own map link.
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number; distanceKm: number } | null>(null);

  // Check if location or selected area is out of active serving locations
  const isOutOfServiceArea = Boolean(
    (locationCoords && locationCoords.distanceKm > 5.0) ||
    (area && (area.toLowerCase().includes('other') || !allowedAreas.includes(area)))
  );
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationNotice, setLocationNotice] = useState<string>('');

  // Keep dropdown and allowed areas reactive when super admin modifies them
  useEffect(() => {
    const handleAreasUpdate = () => {
      const allowed = getAllowedCartAreas();
      setAllowedAreas(allowed);
      setDropdownAreas([...allowed, 'Others (Coming Soon)']);
    };
    window.addEventListener(OPERATIONAL_AREAS_CHANGED_EVENT, handleAreasUpdate);
    return () => window.removeEventListener(OPERATIONAL_AREAS_CHANGED_EVENT, handleAreasUpdate);
  }, []);

  // Location Permission Modal State during checkout
  const [showLocationPermissionModal, setShowLocationPermissionModal] = useState(false);

  // Close permission modal with Escape key
  useEffect(() => {
    if (!showLocationPermissionModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowLocationPermissionModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLocationPermissionModal]);

  // Real-time synchronization with global location changes (navbar or area picker)
  useEffect(() => {
    const handleLocationChange = (e: any) => {
      if (e?.detail) {
        const loc = e.detail;
        // Only update area dropdown if present; NEVER prefill or override exact googleMapsUrl or locationCoords in the checkout cart!
        if (loc.areaName) {
          if (ALLOWED_CART_AREAS.includes(loc.areaName)) {
            setArea(loc.areaName);
          } else if (loc.areaName.toLowerCase().includes('other')) {
            setArea('Others (Coming Soon)');
          }
        }
      }
    };

    window.addEventListener('blishka-location-changed', handleLocationChange);
    return () => window.removeEventListener('blishka-location-changed', handleLocationChange);
  }, []);

  // Listen for browser geolocation permission changes (e.g. user toggles lock icon 🔒 in browser)
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('permissions' in navigator) || !(navigator as any).permissions?.query) {
      return;
    }

    let permStatus: any = null;
    try {
      (navigator as any).permissions.query({ name: 'geolocation' }).then((status: any) => {
        permStatus = status;

        // If permission was already granted, remove any denial error immediately!
        if (status.state === 'granted') {
          setLocationNotice((prev) =>
            prev.includes('blocked') || prev.includes('denied') || prev.includes('permission') ? '' : prev
          );
        }

        const onStateChange = () => {
          if (permStatus.state === 'granted') {
            // Immediately clear any denial notice!
            setLocationNotice('');
            // Do not silently auto-fill coordinates in background; let customer freely tap "Pin Live GPS"
          }
        };

        status.addEventListener('change', onStateChange);
      }).catch(() => {});
    } catch {
      // ignore
    }

    return () => {
      try {
        if (permStatus) {
          permStatus.removeEventListener('change', () => {});
        }
      } catch {
        // ignore
      }
    };
  }, []);

  const handleRequestBookingLocation = () => {
    // Immediately clear any previous error notice so it never lingers
    setLocationNotice('');
    setShowLocationPermissionModal(true);
  };

  const handleConfirmLocationPermission = async () => {
    setShowLocationPermissionModal(false);
    setIsDetectingLocation(true);
    // Immediately clear any old denial notice
    setLocationNotice('');

    // forceFresh: true clears cached permission tokens and enforces maximumAge: 0 to query device afresh
    const res = await detectLiveGeolocation({ forceFresh: true });
    setIsDetectingLocation(false);

    if (res.success && res.lat !== undefined && res.lng !== undefined) {
      const mapsUrl = res.googleMapsUrl || makeGoogleMapsUrl(res.lat, res.lng);
      setGoogleMapsUrl(mapsUrl);
      const dist = res.distanceKm !== undefined ? res.distanceKm : 0;
      setLocationCoords({
        lat: res.lat,
        lng: res.lng,
        distanceKm: dist
      });

      // Update global user location so navbar and whole app knows
      try {
        const locality = findClosestLocality(res.lat, res.lng);
        const isServ = res.isServiceable !== undefined ? res.isServiceable : locality.isServiceable;
        if (isServ && ALLOWED_CART_AREAS.includes(locality.name)) {
          setArea(locality.name);
          setLocationNotice(`✅ GPS Pin Attached (${dist} km from hub)`);
        } else {
          setArea('Others (Coming Soon)');
          setLocationNotice('');
        }
        saveUserLocation({
          status: isServ ? 'serviceable' : 'unserviceable',
          areaName: isServ ? locality.name : 'Others (Coming Soon)',
          lat: res.lat,
          lng: res.lng,
          distanceKm: dist,
          googleMapsUrl: mapsUrl,
          nearestHubName: 'Hari Raj Pure Veg Restaurant Hub',
          source: 'gps',
          updatedAt: new Date().toISOString()
        });
      } catch {
        // ignore
      }
    } else {
      // If customer ALREADY has a location, NEVER show an error!
      if (!googleMapsUrl && !locationCoords) {
        setLocationNotice(res.error || 'Location access blocked. Tap 🔒 near address bar to allow.');
      } else {
        setLocationNotice('');
      }
    }
  };

  // Mobile 10-Digit Validation State (No OTP required)
  const [phoneError, setPhoneError] = useState('');
  const isPhoneValid = phone.length === 10 && /^[6-9]\d{9}$/.test(phone);

  // Submission state
  const [submittedRecord, setSubmittedRecord] = useState<BookingRecord | null>(null);

  // Review & Rating state on booking completion
  const [completionRating, setCompletionRating] = useState<number>(5);
  const [completionHoverRating, setCompletionHoverRating] = useState<number>(0);
  const [completionReviewText, setCompletionReviewText] = useState<string>('');
  const [completionReviewSubmitted, setCompletionReviewSubmitted] = useState<boolean>(false);
  const [isSubmittingCompletionReview, setIsSubmittingCompletionReview] = useState<boolean>(false);

  // Reset to step 1 when opening modal and load saved customer phone if available
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      if (!phone) {
        try {
          const savedPhone = localStorage.getItem('blishka_customer_phone');
          if (savedPhone) setPhone(savedPhone);
        } catch {
          // ignore
        }
      }
      // Auto-prefill name, address, pincode from previous bookings if empty
      try {
        const savedAddrs = getSavedCustomerAddresses();
        if (savedAddrs.length > 0) {
          const latest = savedAddrs[0];
          if (!name && latest.clientName) setName(latest.clientName);
          if (!address && latest.address) setAddress(latest.address);
          if (!pincode && latest.pincode) setPincode(latest.pincode);
          if (latest.area && ALLOWED_CART_AREAS.includes(latest.area)) setArea(latest.area);
        }
      } catch {
        // ignore
      }
    }
  }, [isOpen]);

  // Price & Duration calculations
  const totalPrice = selectedItems.reduce((acc, curr) => acc + curr.price, 0);
  const totalOriginalPrice = selectedItems.reduce((acc, curr) => {
    return acc + (curr.originalPrice || Math.round(curr.price * 1.35));
  }, 0);
  const totalSavings = Math.max(0, totalOriginalPrice - totalPrice);
  const totalEstimatedDurationMinutes = selectedItems.reduce((acc, curr) => {
    return acc + (curr.durationMinutes || 30);
  }, 0);

  // Real-time slot availability for selected date & cart duration
  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;
    setIsLoadingSlots(true);
    setSlotError('');

    getSlotAvailability(preferredDate, totalEstimatedDurationMinutes).then((info) => {
      if (!isMounted) return;
      setSlotsInfo(info);
      setIsLoadingSlots(false);

      // If currently selected slot is not in visible slots for this date, or full, automatically shift to the first available slot!
      const visible = getVisibleSlotsForDate(preferredDate);
      const isCurrentValid = visible.includes(selectedSlot) && info[selectedSlot]?.isAvailable;
      if (!isCurrentValid && visible.length > 0) {
        const firstAvailable = visible.find((s) => info[s]?.isAvailable);
        if (firstAvailable) {
          setSelectedSlot(firstAvailable);
        } else {
          setSelectedSlot(visible[0]);
        }
      }
    });

    return () => {
      isMounted = false;
    };
  }, [preferredDate, isOpen, totalEstimatedDurationMinutes]);

  // If cart becomes empty, ensure user is on step 1
  useEffect(() => {
    if (selectedItems.length === 0 && step === 2) {
      setStep(1);
    }
  }, [selectedItems.length, step]);

  // Re-verify applied coupon discount whenever totalPrice changes
  useEffect(() => {
    if (appliedCoupon) {
      const couponObj = AVAILABLE_COUPONS.find((c) => c.code === appliedCoupon.code);
      if (couponObj) {
        if (totalPrice < couponObj.minOrder) {
          setAppliedCoupon(null);
          setCouponError(`Coupon ${couponObj.code} removed (minimum order value is ₹${couponObj.minOrder}).`);
          setCouponSuccess('');
        } else {
          const disc = couponObj.type === 'smart_tiered'
            ? calculateSmartCouponDiscount(totalPrice)
            : Math.min(couponObj.value, totalPrice);

          if (disc !== appliedCoupon.discount) {
            setAppliedCoupon({ code: couponObj.code, discount: disc });
          }

          const nextTier = getNextSmartCouponTier(totalPrice);
          if (nextTier) {
            setCouponSuccess(`Coupon ${couponObj.code} applied! Saved ₹${disc}. Add ₹${nextTier.needMore} more to save ₹${nextTier.nextDiscount}! 🎉`);
          } else {
            setCouponSuccess(`Coupon ${couponObj.code} applied! Maximum ₹${disc} discount unlocked! 👑`);
          }
        }
      } else {
        setAppliedCoupon(null);
      }
    }
  }, [totalPrice, appliedCoupon]);

  // Auto-apply preselected coupon from Hero voucher ticket if eligible
  useEffect(() => {
    if (isOpen && !appliedCoupon) {
      const savedCode = localStorage.getItem('blishka_selected_coupon');
      if (savedCode && totalPrice >= 500) {
        handleApplyCouponCode(savedCode);
      }
    }
  }, [isOpen, totalPrice]);

  const couponDiscount = appliedCoupon ? appliedCoupon.discount : 0;
  const finalPayable = Math.max(0, totalPrice - couponDiscount);

  // 100% Free Doorstep Travel - Minimum Order Value Rule (₹300)
  const MIN_ORDER_VALUE = 300;
  const isMinOrderMet = finalPayable >= MIN_ORDER_VALUE;
  const minOrderRemaining = Math.max(0, MIN_ORDER_VALUE - finalPayable);

  if (!isOpen) return null;

  // Handle phone input changes with strict 10-digit numeric constraint
  const handlePhoneChange = (val: string) => {
    const clean = val.replace(/\D/g, '').slice(0, 10);
    setPhone(clean);
    if (phoneError) setPhoneError('');
  };

  // Check if a phone number already has past non-cancelled bookings (for first-booking coupons like FIRSTGLOW)
  const checkPhoneHasPastBookings = async (phoneStr: string): Promise<boolean> => {
    const clean = phoneStr.replace(/\D/g, '').slice(-10);
    if (!clean || clean.length < 10) return false;

    // Check local storage bookings first (ignore cancelled)
    const local = getStoredBookings().filter(
      (b) => (b.phone || '').replace(/\D/g, '').slice(-10) === clean &&
             b.status !== 'Cancelled' && b.serviceStatus !== 'cancelled'
    );
    if (local.length > 0) return true;

    // Check remote Cloudflare D1 (ignore cancelled)
    try {
      const remote = await getCustomerBookings(clean);
      const activeRemote = remote.filter(
        (b) => b.status !== 'Cancelled' && b.serviceStatus !== 'cancelled'
      );
      return activeRemote.length > 0;
    } catch {
      return false;
    }
  };

  // Apply Coupon Logic
  const handleApplyCouponCode = async (rawCode: string) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) {
      setCouponError('Please enter a coupon code.');
      setCouponSuccess('');
      return;
    }

    // Map aliases to single smart coupon code
    const resolvedCode = ['FIRSTGLOW', 'BLISHKA250', 'FIRST250', 'GLOW250', 'FIRST100', 'FIRST50'].includes(code)
      ? 'FIRSTGLOW'
      : code;

    const found = AVAILABLE_COUPONS.find((c) => c.code === resolvedCode);
    if (!found) {
      setCouponError('Invalid coupon code. Use FIRSTGLOW for up to ₹250 OFF!');
      setCouponSuccess('');
      return;
    }

    if (totalPrice < found.minOrder) {
      setCouponError(`Add ₹${found.minOrder - totalPrice} more to unlock ₹50 OFF with ${found.code}! (Min order ₹${found.minOrder})`);
      setCouponSuccess('');
      return;
    }

    // Special check for firstBookingOnly coupons: must be first booking from that phone number
    if (found.firstBookingOnly) {
      const activePhone = phone || (typeof localStorage !== 'undefined' ? localStorage.getItem('blishka_customer_phone') || '' : '');
      if (activePhone) {
        const hasPast = await checkPhoneHasPastBookings(activePhone);
        if (hasPast) {
          setCouponError(`${found.code} is valid only on your first booking from this mobile number.`);
          setCouponSuccess('');
          return;
        }
      }
    }

    const disc = found.type === 'smart_tiered'
      ? calculateSmartCouponDiscount(totalPrice)
      : Math.min(found.value, totalPrice);

    setAppliedCoupon({ code: found.code, discount: disc });
    setCouponError('');
    const nextTier = getNextSmartCouponTier(totalPrice);
    if (nextTier) {
      setCouponSuccess(`Coupon ${found.code} applied! Saved ₹${disc}. Add ₹${nextTier.needMore} more to save ₹${nextTier.nextDiscount}! 🎉`);
    } else {
      setCouponSuccess(`Coupon ${found.code} applied! Maximum ₹${disc} discount unlocked! 👑`);
    }
    setCouponInput('');
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
    setCouponSuccess('');
    setCouponInput('');
  };

  // Submission handler on Step 2
  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedItems.length === 0) {
      alert('Please select at least one service before submitting your appointment.');
      setStep(1);
      return;
    }

    if (isOutOfServiceArea) {
      alert('Doorstep parlour care in your Area is Coming Soon! We currently serve within 5 km of Bhilai.');
      return;
    }

    if (!isMinOrderMet) {
      alert(`Minimum booking value of ₹${MIN_ORDER_VALUE} is required for 100% Free Doorstep Parlour Service. Please add ₹${minOrderRemaining} more.`);
      setStep(1);
      return;
    }

    // 10-digit mobile validation
    if (!isPhoneValid) {
      setPhoneError('Please enter a valid 10-digit Indian mobile number (starts with 6, 7, 8, or 9).');
      return;
    }

    // Double check first-booking coupons before final submission
    const activeCouponObj = appliedCoupon ? AVAILABLE_COUPONS.find((c) => c.code === appliedCoupon.code) : null;
    if (activeCouponObj?.firstBookingOnly) {
      const hasPast = await checkPhoneHasPastBookings(phone);
      if (hasPast) {
        setAppliedCoupon(null);
        alert(`Coupon ${activeCouponObj.code} is valid only on your first booking from this mobile number. The discount has been removed.`);
        return;
      }
    }

    const tomorrowStr = getLocalTomorrowDateString();
    if (preferredDate < tomorrowStr) {
      setSlotError(`Doorstep salon appointments are available from tomorrow onwards.`);
      setPreferredDate(tomorrowStr);
      return;
    }

    const isToday = preferredDate === getLocalTodayDateString();
    if (isToday && !isSlotAvailableTimeWise(selectedSlot, preferredDate)) {
      setSlotError(`Selected time slot (${selectedSlot}) is within advance notice limit or has ended. Please choose an upcoming slot.`);
      return;
    }

    // Double check if selected slot is full or consecutive slot blocked
    const slotData = slotsInfo[selectedSlot];
    if (slotData && !slotData.isAvailable) {
      const nextSlot = isLongServiceOrder(totalEstimatedDurationMinutes) ? getNextSlot(selectedSlot) : null;
      setSlotError(
        nextSlot
          ? `Time slot (${selectedSlot}) or consecutive slot (${nextSlot}) on ${preferredDate} cannot accommodate this appointment. Please choose another time slot.`
          : `Time slot (${selectedSlot}) on ${preferredDate} is fully booked. Please choose another time slot.`
      );
      return;
    }

    // Select available beautician via fair round-robin (accounting for long orders)
    const roundRobin = selectRoundRobinBeautician(preferredDate, selectedSlot, totalEstimatedDurationMinutes);
    const assignedStaff = roundRobin?.beautician;

    const refId = 'BLISHKA-' + Math.floor(100000 + Math.random() * 900000);
    const startOtp = Math.floor(1000 + Math.random() * 9000).toString();
    const now = new Date().toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });

    const record: BookingRecord = {
      bookingRef: refId,
      timestamp: now,
      clientName: name,
      phone,
      area,
      address,
      pincode: pincode.trim(),
      googleMapsUrl: googleMapsUrl.trim(),
      latitude: locationCoords?.lat,
      longitude: locationCoords?.lng,
      distanceKm: locationCoords?.distanceKm,
      preferredDate,
      preferredSlot: selectedSlot,
      specialRequests: specialRequests.trim(),
      couponCode: appliedCoupon?.code,
      couponDiscount: appliedCoupon?.discount,
      status: 'Confirmed',
      serviceStatus: assignedStaff ? 'assigned' : 'confirmed',
      servicePerson: assignedStaff ? `${assignedStaff.name} (${assignedStaff.specialty})` : 'Professional Beautician',
      partnerId: assignedStaff?.id,
      partnerName: assignedStaff?.name,
      partnerPhone: assignedStaff?.phone,
      startOtp,
      paymentMethod: 'Pay After Service',
      paymentStatus: 'Pending Collection',
      items: selectedItems,
      totalPrice: finalPayable,
      totalOriginalPrice,
      totalSavings
    };

    setIsSubmitting(true);
    const bookingResult = await createBookingOrder(record);
    setIsSubmitting(false);

    if (!bookingResult.success && bookingResult.slotFull) {
      setSlotError(bookingResult.error || `Time slot ${selectedSlot} on ${preferredDate} is fully booked. Please choose another slot.`);
      const fresh = await getSlotAvailability(preferredDate);
      setSlotsInfo(fresh);
      return;
    }

    if (bookingResult.assignedPartner) {
      record.partnerId = bookingResult.assignedPartner.id;
      record.partnerName = bookingResult.assignedPartner.name;
      record.partnerPhone = bookingResult.assignedPartner.phone;
      record.servicePerson = `${bookingResult.assignedPartner.name} (${bookingResult.assignedPartner.specialty || 'Beauty Specialist'})`;
      record.serviceStatus = 'assigned';
    }

    try {
      localStorage.setItem('blishka_customer_phone', phone);
      saveCustomerAddress({
        clientName: name,
        phone,
        address,
        area,
        pincode,
        lat: locationCoords?.lat,
        lng: locationCoords?.lng,
        googleMapsUrl,
        isServiceable: true
      });
    } catch {
      // ignore
    }
    setSubmittedRecord(record);
  };

  const handleSendViaWhatsApp = (rec: BookingRecord) => {
    const totalDurationMins = rec.items?.reduce((acc: number, s: any) => acc + (s.durationMinutes || 30), 0) || 30;
    const startHourText = rec.preferredSlot ? rec.preferredSlot.split(' - ')[0] : '';
    const finishHourText = startHourText ? calculateEndTime(startHourText, totalDurationMins) : '';
    const windowText = finishHourText ? ` (~${finishHourText} finish)` : '';

    const servicesText = rec.items
      .map((s: any) => `• ${s.name} (${formatDuration(s.durationMinutes || 30)} - ₹${s.price})`)
      .join('\n');

    const couponLine = rec.couponCode
      ? `*Coupon Applied:* ${rec.couponCode} (-₹${rec.couponDiscount || 0})\n`
      : '';

    const msg = encodeURIComponent(
      `🌸 *New Blishka Home Parlour Booking* 🌸\n` +
      `*Booking Ref:* ${rec.bookingRef}\n` +
      `*🔒 Doorstep Start OTP:* ${rec.startOtp}\n\n` +
      `*Client:* ${rec.clientName}\n` +
      `*Verified Phone:* +91 ${rec.phone}\n` +
      `*City / Area:* ${rec.area}\n` +
      `*Full Address:* ${rec.address} ${rec.pincode ? `(${rec.pincode})` : ''}\n` +
      (rec.googleMapsUrl ? `*📍 Google Maps Location:* ${rec.googleMapsUrl}\n` : '') +
      `*Preferred Date:* ${rec.preferredDate}\n` +
      `*Time Slot:* ${rec.preferredSlot}${windowText} (~${formatDuration(totalDurationMins)})\n` +
      (rec.servicePerson ? `*👩‍🦰 Assigned Beautician:* ${rec.servicePerson}\n\n` : '\n') +
      `*Selected Services:*\n${servicesText}\n\n` +
      couponLine +
      `*Total Payable at Home:* ₹${rec.totalPrice} (Pay After Service via Cash / UPI)\n` +
      `*Special Notes:* ${rec.specialRequests || 'None'}\n\n` +
      `Please confirm slot availability. Thank you! ♡`
    );

    window.open(`https://wa.me/${CONTACT_INFO.whatsapp}?text=${msg}`, '_blank');
  };

  const handleBookingReviewSubmit = async () => {
    if (!submittedRecord || !completionReviewText.trim()) return;
    setIsSubmittingCompletionReview(true);
    try {
      await submitReview({
        bookingRef: submittedRecord.bookingRef,
        customerName: submittedRecord.clientName,
        customerPhone: submittedRecord.phone,
        rating: completionRating,
        reviewText: completionReviewText.trim(),
        serviceNames: submittedRecord.items?.map((i: any) => i.name).join(', '),
        area: submittedRecord.area
      });
      setCompletionReviewSubmitted(true);
    } catch (e) {
      console.warn('Error submitting review from booking screen:', e);
    } finally {
      setIsSubmittingCompletionReview(false);
    }
  };

  const handleDone = () => {
    setSubmittedRecord(null);
    setStep(1);
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
    setCouponSuccess('');
    setCompletionRating(5);
    setCompletionHoverRating(0);
    setCompletionReviewText('');
    setCompletionReviewSubmitted(false);
    setIsSubmittingCompletionReview(false);
    onClearCart();
    onClose();
  };

  if (!isOpen) return null;

  const cardContent = (
    <div className={`booking-modal-card cart-modal-container ${isPageView ? 'cart-page-card' : ''}`} onClick={(e) => e.stopPropagation()}>
      <button className="modal-close-btn" onClick={onClose} aria-label={isPageView ? 'Back to Home' : 'Close modal'}>
        <X size={20} />
      </button>

        {/* ── SUCCESS SCREEN ── */}
        {submittedRecord ? (
          <div className="booking-success-view">
            <div className="success-icon-badge">
              <CheckCircle2 size={56} />
            </div>

            <h3 className="success-title">Appointment Request Received!</h3>

            <p className="success-desc">
              Thank you, <strong>{submittedRecord.clientName}</strong>! Your doorstep beauty appointment has been registered with reference ID:
            </p>

            <div className="success-ref-pill">
              <span>Ref ID:</span>
              <strong>{submittedRecord.bookingRef}</strong>
            </div>

            {/* Doorstep Start OTP Card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(125, 26, 56, 0.06) 0%, rgba(216, 131, 149, 0.12) 100%)',
              border: '1.5px dashed var(--blishka-berry)',
              borderRadius: '16px',
              padding: '1rem',
              margin: '1rem 0 1.25rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--blishka-berry)', fontWeight: 700, marginBottom: '0.25rem' }}>
                🔒 Doorstep Service Start OTP
              </div>
              <div style={{ fontSize: '2.1rem', fontWeight: 800, letterSpacing: '6px', color: 'var(--blishka-berry)', fontFamily: 'monospace' }}>
                {submittedRecord.startOtp}
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-body)', margin: '0.35rem 0 0', lineHeight: 1.4 }}>
                Share this 4-digit code with your beautician when she reaches your doorstep to start your service.
              </p>
            </div>

            <div className="success-summary-box">
              <div className="summary-row">
                <span>Appointment Date:</span>
                <strong>{submittedRecord.preferredDate}</strong>
              </div>
              <div className="summary-row">
                <span>Time Slot & Window:</span>
                <strong>
                  {submittedRecord.preferredSlot}
                  {(() => {
                    const totalMins = submittedRecord.items?.reduce((acc: number, s: any) => acc + (s.durationMinutes || 30), 0) || 30;
                    const startPart = submittedRecord.preferredSlot ? submittedRecord.preferredSlot.split(' - ')[0] : '';
                    const finishPart = startPart ? calculateEndTime(startPart, totalMins) : '';
                    return finishPart ? ` (~${finishPart} finish, ~${formatDuration(totalMins)})` : '';
                  })()}
                </strong>
              </div>
              <div className="summary-row" style={{ color: 'var(--blishka-berry)' }}>
                <span>Assigned Beautician:</span>
                <strong>{submittedRecord.servicePerson || 'Professional Beautician'}</strong>
              </div>
              <div className="summary-row">
                <span>Verified Contact:</span>
                <strong>+91 {submittedRecord.phone}</strong>
              </div>
              <div className="summary-row">
                <span>Address:</span>
                <span>{submittedRecord.address}, {submittedRecord.area}</span>
              </div>
              <div className="summary-row" style={{ color: '#2e7d32' }}>
                <span>Payment Mode:</span>
                <strong>Pay After Service (Cash / UPI)</strong>
              </div>
              {submittedRecord.couponCode && (
                <div className="summary-row" style={{ color: '#2e7d32' }}>
                  <span>Coupon Applied ({submittedRecord.couponCode}):</span>
                  <strong>-₹{submittedRecord.couponDiscount || 0}</strong>
                </div>
              )}
              <div className="summary-row" style={{ borderTop: '1px dashed rgba(125,26,56,0.15)', paddingTop: '0.4rem', marginTop: '0.4rem' }}>
                <span>Total Payable at Home:</span>
                <strong style={{ color: 'var(--blishka-berry)', fontSize: '1.05rem' }}>₹{submittedRecord.totalPrice}</strong>
              </div>
            </div>

            <p style={{ fontSize: '0.88rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              Assigned Beautician: <strong>{submittedRecord.servicePerson}</strong>. Our team will arrive at your scheduled time with single-use sanitized disposables.
            </p>

            {/* ── Rate & Review Doorstep Experience ── */}
            <div className="booking-success-review-box" style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, rgba(125, 26, 56, 0.05) 100%)',
              border: '1.5px solid rgba(245, 158, 11, 0.28)',
              borderRadius: '16px',
              padding: '1.2rem',
              margin: '0 0 1.25rem',
              textAlign: 'center'
            }}>
              {completionReviewSubmitted ? (
                <div style={{ padding: '0.35rem 0' }}>
                  <div style={{ color: '#2e7d32', marginBottom: '0.35rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}>
                    <CheckCircle2 size={22} />
                    <strong style={{ fontSize: '0.98rem' }}>Review Published!</strong>
                  </div>
                  <p style={{ fontSize: '0.84rem', color: 'var(--color-text-body)', margin: '0 0 0.5rem', lineHeight: 1.4 }}>
                    Thank you, <strong>{submittedRecord.clientName}</strong>! Your <strong>{completionRating} ★</strong> review has been added to our homepage.
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', color: '#f59e0b' }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={18} fill={s <= completionRating ? '#f59e0b' : 'none'} color="#f59e0b" />
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: '#d97706',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.35rem'
                  }}>
                    <Sparkles size={12} />
                    <span>Rate Your Experience</span>
                  </div>
                  <h4 style={{ fontSize: '1.02rem', fontWeight: 800, color: 'var(--color-text-title)', margin: '0 0 0.3rem' }}>
                    How was your booking experience?
                  </h4>
                  <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: '0 0 0.65rem' }}>
                    Tap stars to rate & leave feedback for women in Bhilai & Durg!
                  </p>

                  {/* Interactive Stars */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '0.7rem' }}>
                    {[1, 2, 3, 4, 5].map((starVal) => {
                      const isFilled = (completionHoverRating || completionRating) >= starVal;
                      return (
                        <button
                          key={starVal}
                          type="button"
                          onMouseEnter={() => setCompletionHoverRating(starVal)}
                          onMouseLeave={() => setCompletionHoverRating(0)}
                          onClick={() => setCompletionRating(starVal)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            transform: (completionHoverRating || completionRating) === starVal ? 'scale(1.15)' : 'scale(1)',
                            transition: 'transform 0.15s ease'
                          }}
                          title={`${starVal} Star`}
                        >
                          <Star
                            size={28}
                            fill={isFilled ? '#f59e0b' : 'none'}
                            color={isFilled ? '#f59e0b' : '#d1d5db'}
                          />
                        </button>
                      );
                    })}
                  </div>

                  {/* Review Textarea */}
                  <textarea
                    rows={2}
                    value={completionReviewText}
                    onChange={(e) => setCompletionReviewText(e.target.value)}
                    placeholder="Write a quick line (e.g. Easy booking, excited for doorstep parlour at home!)..."
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      borderRadius: '10px',
                      border: '1px solid rgba(125, 26, 56, 0.2)',
                      fontSize: '0.84rem',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      marginBottom: '0.6rem'
                    }}
                  />

                  <button
                    type="button"
                    disabled={isSubmittingCompletionReview || !completionReviewText.trim()}
                    onClick={handleBookingReviewSubmit}
                    className="btn btn-berry"
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      opacity: (!completionReviewText.trim() || isSubmittingCompletionReview) ? 0.6 : 1,
                      cursor: (!completionReviewText.trim() || isSubmittingCompletionReview) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSubmittingCompletionReview ? 'Submitting...' : 'Submit Rating & Review ★'}
                  </button>
                </div>
              )}
            </div>

            <div className="success-actions">
              <button
                type="button"
                onClick={() => handleSendViaWhatsApp(submittedRecord)}
                className="btn btn-whatsapp"
                style={{ width: '100%', gap: '0.5rem' }}
              >
                <MessageCircle size={17} />
                <span>Confirm Instantly on WhatsApp</span>
              </button>

              <button
                type="button"
                onClick={handleDone}
                className="btn btn-berry"
                style={{ width: '100%' }}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* ── CART HEADER & STEPPER ── */}
            <div className="cart-modal-header">
              <div className="cart-header-left">
                {step === 2 ? (
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="cart-header-back-btn"
                    title="Back to cart items"
                    aria-label="Back to cart items"
                  >
                    <ArrowLeft size={18} />
                  </button>
                ) : (
                  <div className="cart-header-icon-wrap">
                    <ShoppingBag size={20} />
                  </div>
                )}
                <div>
                  <h3 className="cart-modal-heading">
                    {step === 1 ? 'Your Cart' : 'Slot & Address'}
                  </h3>
                  <p className="cart-modal-sub">
                    {step === 1
                      ? `${selectedItems.length} ${selectedItems.length === 1 ? 'service' : 'services'} selected`
                      : 'Choose appointment timing & doorstep address'}
                  </p>
                </div>
              </div>

              {/* Progress Stepper Pills */}
              {selectedItems.length > 0 && (
                <div className="cart-stepper-pills">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className={`cart-step-pill ${step === 1 ? 'active' : 'completed'}`}
                  >
                    <span className="step-pill-num">{step === 2 ? '✓' : '1'}</span>
                    <span className="step-pill-label">Services</span>
                  </button>
                  <span className="step-divider" />
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedItems.length > 0) setStep(2);
                    }}
                    className={`cart-step-pill ${step === 2 ? 'active' : ''}`}
                  >
                    <span className="step-pill-num">2</span>
                    <span className="step-pill-label">Details</span>
                  </button>
                </div>
              )}
            </div>

            {/* ══════════════════════════════════════════════════════════════
                PAGE 1: SERVICES REVIEW, HAVE A COUPON?, AND TOTAL COST
               ══════════════════════════════════════════════════════════════ */}
            {step === 1 && (
              <div className="cart-form-scrollable">
                <div className="checkout-section-card">
                  <div className="checkout-section-title">
                    <span className="section-step-num">1</span>
                    <h4>Review Selected Services ({selectedItems.length})</h4>
                    {selectedItems.length > 0 && (
                      <button
                        type="button"
                        onClick={onClearCart}
                        className="cart-clear-link"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  {selectedItems.length === 0 ? (
                    <div className="cart-empty-view">
                      <ShoppingBag size={38} className="cart-empty-icon" />
                      <p className="cart-empty-title">Your cart is currently empty</p>
                      <p className="cart-empty-sub">Explore our service menu or curated bundles to select doorstep beauty services.</p>
                      <div className="cart-empty-btn-group" style={{ display: 'flex', gap: '0.6rem', marginTop: '0.85rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof window !== 'undefined') {
                              window.location.href = '/services';
                            }
                          }}
                          className="btn btn-berry btn-sm"
                        >
                          <span>Explore Services Menu</span>
                          <ArrowRight size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof window !== 'undefined') {
                              window.location.href = '/bundles';
                            }
                          }}
                          className="btn btn-outline btn-sm"
                        >
                          <span>View Curated Bundles</span>
                          <ArrowRight size={14} />
                        </button>
                      </div>
                      <div className="cart-empty-recom-wrap">
                        {renderPeopleAlsoLike()}
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Service Items List */}
                      <div className="cart-items-list">
                        {selectedItems.map((item) => {
                          const itemOrig = item.originalPrice || Math.round(item.price * 1.35);
                          const itemSavings = itemOrig - item.price;
                          return (
                            <div key={item.id} className="cart-item-row">
                              <div className="cart-item-info">
                                <span className="cart-item-name">{item.name}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '2px', flexWrap: 'wrap' }}>
                                  {item.category && (
                                    <span className="cart-item-cat">
                                      {item.category.replace('_', ' ')}
                                    </span>
                                  )}
                                  <span className="cart-item-duration-pill">
                                    <Clock size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />
                                    {formatDuration(item.durationMinutes)}
                                  </span>
                                </div>
                              </div>

                              <div className="cart-item-pricing">
                                <div className="cart-item-price-row">
                                  <span className="cart-item-orig">₹{itemOrig}</span>
                                  <span className="cart-item-price">₹{item.price}</span>
                                </div>
                                {itemSavings > 0 && (
                                  <span className="cart-item-save">Save ₹{itemSavings}</span>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={() => onRemoveItem(item.id)}
                                className="cart-item-remove-btn"
                                title={`Remove ${item.name}`}
                                aria-label={`Remove ${item.name}`}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      {/* ── PEOPLE ALSO LIKE (RECOMMENDED LOW-BUDGET SERVICES) ── */}
                      {renderPeopleAlsoLike()}

                      {/* ── HAVE A COUPON? SECTION ── */}
                      <div className="cart-coupon-card">
                        <div className="coupon-header-row">
                          <div className="coupon-title-left">
                            <Tag size={15} className="coupon-title-icon" />
                            <span className="coupon-title-text">Have a coupon?</span>
                          </div>
                          {appliedCoupon && (
                            <span className="coupon-active-tag">
                              <Check size={12} />
                              Active
                            </span>
                          )}
                        </div>

                        {!appliedCoupon ? (
                          <div className="coupon-input-wrap">
                            <input
                              type="text"
                              value={couponInput}
                              onChange={(e) => {
                                setCouponInput(e.target.value.toUpperCase());
                                if (couponError) setCouponError('');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleApplyCouponCode(couponInput);
                                }
                              }}
                              placeholder="Enter coupon code"
                              className="coupon-code-input"
                              maxLength={15}
                            />
                            <button
                              type="button"
                              onClick={() => handleApplyCouponCode(couponInput)}
                              disabled={!couponInput.trim()}
                              className="coupon-apply-btn"
                            >
                              Apply
                            </button>
                          </div>
                        ) : (
                          <div className="coupon-applied-pill">
                            <div className="coupon-applied-info">
                              <CheckCircle2 size={16} className="coupon-applied-check" />
                              <div>
                                <span className="coupon-applied-code">{appliedCoupon.code}</span>
                                <span className="coupon-applied-savings">
                                  Saved ₹{appliedCoupon.discount} on this booking
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveCoupon}
                              className="coupon-remove-btn"
                              title="Remove coupon"
                            >
                              Remove
                            </button>
                          </div>
                        )}

                        {couponError && (
                          <span className="coupon-feedback error">{couponError}</span>
                        )}
                        {couponSuccess && (
                          <span className="coupon-feedback success">{couponSuccess}</span>
                        )}

                        {!appliedCoupon && (
                          <div className="coupon-suggestions">
                            <span className="coupon-suggestions-label">Available Coupons:</span>
                            {AVAILABLE_COUPONS.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                className="coupon-chip"
                                onClick={() => handleApplyCouponCode(c.code)}
                                title={c.description}
                              >
                                <strong>{c.code}</strong>
                                <span className="coupon-chip-benefit">{c.label} (above ₹{c.minOrder})</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* ── SIMPLE MINIMUM ORDER NOTICE ── */}
                      {!isMinOrderMet ? (
                        <div className="cart-min-order-simple">
                          <Truck size={15} className="min-order-simple-icon" />
                          <span>Min. booking value is <strong>₹{MIN_ORDER_VALUE}</strong> for free doorstep service (add <strong>₹{minOrderRemaining}</strong> more)</span>
                        </div>
                      ) : (
                        <div className="cart-min-order-simple met">
                          <CheckCircle2 size={14} className="min-order-simple-icon" />
                          <span>100% Free doorstep service unlocked (Min. ₹{MIN_ORDER_VALUE} met)</span>
                        </div>
                      )}

                      {/* ── TOTAL COST (Cart Bill Summary) ── */}
                      <div className="cart-bill-summary">
                        <div className="bill-row">
                          <span>Salon Value:</span>
                          <span className="bill-strikethrough">₹{totalOriginalPrice.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="bill-row bill-savings-highlight">
                          <span>Your Savings:</span>
                          <span className="bill-save-badge">- ₹{totalSavings.toLocaleString('en-IN')}</span>
                        </div>
                        {appliedCoupon && (
                          <div className="bill-row bill-coupon-highlight">
                            <span>
                              <Tag size={13} />
                              Coupon ({appliedCoupon.code}):
                            </span>
                            <span className="bill-coupon-badge">- ₹{appliedCoupon.discount.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                        <div className="bill-row">
                          <span>
                            <Truck size={14} style={{ color: '#166534' }} />
                            Doorstep Visit Fee:
                          </span>
                          <span style={{ fontWeight: 700, color: '#166534' }}>FREE</span>
                        </div>
                        <div className="bill-row">
                          <span>
                            <Clock size={13} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle', color: 'var(--blishka-berry)' }} />
                            Est. Service Time:
                          </span>
                          <span style={{ fontWeight: 700, color: 'var(--blishka-berry)' }}>~{formatDuration(totalEstimatedDurationMinutes)}</span>
                        </div>
                        <div className="bill-row">
                          <span>Payment Mode:</span>
                          <span style={{ fontWeight: 700 }}>Pay After Service</span>
                        </div>
                        <div className="bill-row bill-total-row">
                          <span>Final Total:</span>
                          <span className="bill-total-price">₹{finalPayable.toLocaleString('en-IN')}</span>
                        </div>
                      </div>

                      {/* Pay After Service Assurance Badge */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.65rem',
                        background: 'rgba(92, 124, 100, 0.08)',
                        border: '1px solid rgba(92, 124, 100, 0.25)',
                        borderRadius: '12px',
                        padding: '0.75rem 0.95rem',
                        margin: '0.85rem 0 1.25rem',
                        fontSize: '0.82rem',
                        color: '#2e7d32',
                        lineHeight: 1.45
                      }}>
                        <ShieldCheck size={20} style={{ flexShrink: 0, color: '#2e7d32' }} />
                        <span><strong>100% Pay After Service:</strong> No advance payment required. Pay comfortably by Cash or direct UPI to your beautician after your service is fully completed to your delight.</span>
                      </div>

                      {/* Page 1 CTA: Proceed to Slot & Address */}
                      <div className="cart-step1-action">
                        {isMinOrderMet ? (
                          <button
                            type="button"
                            onClick={() => {
                              setStep(2);
                              const container = document.querySelector('.cart-modal-container');
                              if (container) container.scrollTop = 0;
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="btn btn-berry cart-continue-btn"
                          >
                            <span>Continue to Slot & Address</span>
                            <ArrowRight size={17} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled
                            style={{
                              width: '100%',
                              padding: '0.85rem',
                              borderRadius: '12px',
                              border: 'none',
                              background: '#f1f5f9',
                              color: 'var(--color-text-muted)',
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              cursor: 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.45rem'
                            }}
                          >
                            <AlertCircle size={16} />
                            <span>Add ₹{minOrderRemaining} more (Min. ₹{MIN_ORDER_VALUE})</span>
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                PAGE 2: SLOT SELECTION & ADDRESS IN NEXT PAGE OF CART
               ══════════════════════════════════════════════════════════════ */}
            {step === 2 && (
              <form onSubmit={handleSubmitBooking} className="cart-form-scrollable">
                {/* Mini Order Summary Recap Bar */}
                <div className="cart-step2-recap-bar">
                  <div className="step2-recap-info">
                    <span className="step2-recap-count">
                      {selectedItems.length} {selectedItems.length === 1 ? 'service' : 'services'} • ~{formatDuration(totalEstimatedDurationMinutes)}
                    </span>
                    <span className="step2-recap-total">
                      Total: <strong>₹{finalPayable.toLocaleString('en-IN')}</strong>
                      {appliedCoupon && <span className="step2-coupon-tag">({appliedCoupon.code})</span>}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="step2-edit-cart-btn"
                  >
                    Edit Cart
                  </button>
                </div>

                {/* ── SECTION 2: PREFERRED TIME SLOT ── */}
                <div className="checkout-section-card">
                  <div className="checkout-section-title">
                    <span className="section-step-num">2</span>
                    <h4>Choose Date & Preferred Time Slot</h4>
                  </div>

                  <div className="form-group" style={{ marginBottom: '1.15rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label className="form-label" htmlFor="appointment-date" style={{ margin: 0 }}>
                        <Calendar size={13} style={{ display: 'inline', marginRight: '4px' }} />
                        Appointment Date *
                      </label>
                    </div>
                    <input
                      id="appointment-date"
                      type="date"
                      required
                      min={getLocalTomorrowDateString()}
                      className="form-input"
                      value={preferredDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        const tomorrow = getLocalTomorrowDateString();
                        if (val && val < tomorrow) {
                          setSlotError(`Doorstep salon appointments are available from tomorrow onwards.`);
                          setPreferredDate(tomorrow);
                        } else {
                          setSlotError('');
                          setPreferredDate(val);
                        }
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label className="form-label" style={{ margin: 0 }}>
                        <Clock size={13} style={{ display: 'inline', marginRight: '4px' }} />
                        Preferred Doorstep Slot (10:00 AM – 6:00 PM) *
                      </label>
                      {isLoadingSlots && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                          Checking availability...
                        </span>
                      )}
                    </div>

                    {/* Today's Advance Notice Notice if selected */}
                    {preferredDate === getLocalTodayDateString() && (
                      (() => {
                        const visible = getVisibleSlotsForDate(preferredDate);
                        if (visible.length > 0) {
                          return (
                            <div style={{ fontSize: '0.74rem', color: 'var(--blishka-berry)', fontWeight: 600, marginBottom: '0.45rem' }}>
                              ⚡ Slots available from <strong>{visible[0]}</strong> onwards (minimum notice for beautician dispatch).
                            </div>
                          );
                        }
                        return null;
                      })()
                    )}

                    {/* Long Service Notice (> 2:30 hours reserves next slot) */}
                    {isLongServiceOrder(totalEstimatedDurationMinutes) && (
                      <div
                        style={{
                          background: '#fff7ed',
                          border: '1px solid #fed7aa',
                          borderRadius: '8px',
                          padding: '0.55rem 0.75rem',
                          fontSize: '0.78rem',
                          color: '#9a3412',
                          marginBottom: '0.65rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.45rem'
                        }}
                      >
                        <Clock size={15} style={{ flexShrink: 0 }} />
                        <span>
                          <strong>Comprehensive Care Notice:</strong> Since your order takes ~{formatDuration(totalEstimatedDurationMinutes)} (over 2.5 hours), the consecutive slot ({selectedSlot ? (getNextSlot(selectedSlot) || 'next window') : 'next window'}) will also be reserved exclusively for your beautician.
                        </span>
                      </div>
                    )}

                    {slotError && (
                      <div
                        style={{
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.78rem',
                          color: '#dc2626',
                          marginBottom: '0.6rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}
                      >
                        <AlertCircle size={14} />
                        <span>{slotError}</span>
                      </div>
                    )}

                    {(() => {
                      const isToday = preferredDate === getLocalTodayDateString();
                      const visibleSlots = getVisibleSlotsForDate(preferredDate);

                      if (isToday && visibleSlots.length === 0) {
                        return (
                          <div
                            style={{
                              background: '#fff7ed',
                              border: '1px solid #fed7aa',
                              borderRadius: '10px',
                              padding: '0.85rem 1rem',
                              fontSize: '0.82rem',
                              color: '#9a3412',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.6rem',
                              marginTop: '0.25rem'
                            }}
                          >
                            <Clock size={16} />
                            <div>
                              <strong>Today's doorstep service slots have ended.</strong>
                              <div style={{ marginTop: '2px', fontSize: '0.76rem' }}>
                                Please select tomorrow or an upcoming date for your parlour care.
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="time-slots-grid">
                          {visibleSlots.map((time) => {
                            const isChecked = selectedSlot === time;
                            const sInfo = slotsInfo[time];
                            const isFull = sInfo ? !sInfo.isAvailable : false;

                            return (
                              <button
                                key={time}
                                type="button"
                                disabled={isFull}
                                className={`time-slot-chip ${isChecked ? 'active' : ''} ${isFull ? 'disabled' : ''}`}
                                onClick={() => {
                                  if (!isFull) {
                                    setSelectedSlot(time);
                                    setSlotError('');
                                  }
                                }}
                                role="radio"
                                aria-checked={isChecked}
                                aria-disabled={isFull}
                                title={
                                  isFull
                                    ? 'Slot Full / Unavailable'
                                    : 'Available'
                                }
                              >
                                <span>{time}</span>
                                {isFull && (
                                  <span className="slot-chip-badge full">Full</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {selectedSlot && (
                      <div className="slot-duration-estimate-box">
                        <div className="slot-duration-estimate-header">
                          <Clock size={15} className="slot-duration-icon" />
                          <span>Estimated Doorstep Schedule:</span>
                          <strong className="slot-duration-window-text">
                            Arrival {selectedSlot} · Service finishes ~{calculateEndTime(selectedSlot.split(' - ')[0] || selectedSlot, totalEstimatedDurationMinutes)}
                          </strong>
                        </div>
                        <span className="slot-duration-estimate-sub">
                          ~{formatDuration(totalEstimatedDurationMinutes)} doorstep salon care for {selectedItems.length} {selectedItems.length === 1 ? 'service' : 'services'}.
                          {isLongServiceOrder(totalEstimatedDurationMinutes) && getNextSlot(selectedSlot) && (
                            <span style={{ display: 'block', color: 'var(--blishka-berry)', fontWeight: 600, marginTop: '2px' }}>
                              ✓ Consecutive slot ({getNextSlot(selectedSlot)}) reserved to guarantee unhurried pampering.
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── SECTION 3: ADDRESS & CUSTOMER DETAILS ── */}
                <div className="checkout-section-card">
                  <div className="checkout-section-title">
                    <span className="section-step-num">3</span>
                    <h4>Customer & Contact Details</h4>
                  </div>

                  <div className="form-row-2col">
                    <div className="form-group">
                      <label className="form-label" htmlFor="client-name">Your Full Name *</label>
                      <input
                        id="client-name"
                        type="text"
                        required
                        placeholder="e.g. Priya Sharma"
                        className="form-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>

                    {/* 10-Digit Mobile Number (No OTP Required) */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                        <label className="form-label" htmlFor="client-phone" style={{ margin: 0 }}>
                          <Smartphone size={12} style={{ display: 'inline', marginRight: '3px' }} />
                          Mobile Number (10 Digits) *
                        </label>
                        {isPhoneValid && (
                          <span className="phone-verified-tag" style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                            <CheckCircle2 size={12} />
                            <span>10 Digits Valid</span>
                          </span>
                        )}
                      </div>

                      <div className="phone-input-wrapper">
                        <span className="phone-prefix">+91</span>
                        <input
                          id="client-phone"
                          type="tel"
                          required
                          placeholder="Enter 10-digit mobile number"
                          className="form-input phone-field"
                          value={phone}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          maxLength={10}
                        />
                      </div>

                      {/* Digits counter hint */}
                      {phone.length > 0 && phone.length < 10 && (
                        <span className="phone-length-hint" style={{ color: '#d97706', fontSize: '0.78rem', marginTop: '4px', display: 'block' }}>
                          {10 - phone.length} more {10 - phone.length === 1 ? 'digit' : 'digits'} required
                        </span>
                      )}

                      {/* Error Message */}
                      {phoneError && (
                        <span className="otp-error-msg" style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                          {phoneError}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="client-area">Service Area / Sector *</label>
                    <select
                      id="client-area"
                      className="form-select"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                    >
                      {dropdownAreas.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="client-address">
                      <MapPin size={13} style={{ display: 'inline', marginRight: '4px' }} />
                      Complete Home Address *
                    </label>
                    <textarea
                      id="client-address"
                      required
                      rows={2}
                      placeholder="House/Flat No., Apartment Name, Street, Landmark..."
                      className="form-textarea"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>

                  {/* ── GOOGLE MAPS PINPOINT LOCATION FIELD ── */}
                  <div className="booking-maps-pin-card">
                    <div className="maps-pin-header">
                      <div className="maps-pin-header-left">
                        <Compass size={16} color="var(--blishka-berry)" />
                        <div>
                          <span className="maps-pin-title">Exact Google Maps Location Pin</span>
                          <span className="maps-pin-sub">Gives the beautician 1-tap navigation directly to your doorstep</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleRequestBookingLocation}
                        disabled={isDetectingLocation}
                        className="btn-detect-maps-gps"
                        id="checkout-pin-live-gps-btn"
                        title="Ask for location permission and pin live GPS"
                      >
                        <Navigation size={12} className={isDetectingLocation ? 'spin-pulse' : ''} />
                        <span>{isDetectingLocation ? 'Detecting...' : 'Pin Live GPS'}</span>
                      </button>
                    </div>

                    <div className="maps-pin-input-group">
                      <input
                        type="url"
                        placeholder="Paste Google Maps link (or tap 'Pin Live GPS')"
                        className="form-input maps-url-field"
                        value={googleMapsUrl}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGoogleMapsUrl(val);
                          // Immediately clear any denial notice when customer types or pastes link
                          if (val || locationNotice.includes('blocked') || locationNotice.includes('denied') || locationNotice.includes('permission')) {
                            setLocationNotice('');
                          }
                        }}
                      />
                      {googleMapsUrl && (
                        <a
                          href={googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="maps-preview-link"
                          title="Open pinned location on Google Maps"
                        >
                          <ExternalLink size={12} />
                          <span>Verify Pin</span>
                        </a>
                      )}
                    </div>

                    {/* Out of serving area Red Alert Strip inside map pin card */}
                    {isOutOfServiceArea && (
                      <div className="loc-out-of-area-red-alert checkout-out-of-area-strip" role="alert">
                        <span className="loc-red-dot">🔴</span>
                        <span className="loc-red-title">
                          Doorstep parlour care in your Area is Coming Soon!
                        </span>
                      </div>
                    )}

                    {/* Success Notice: Shown only when location is pinned and within serviceable area */}
                    {!isOutOfServiceArea && locationNotice && !locationNotice.includes('blocked') && !locationNotice.includes('denied') && !locationNotice.includes('permission') && (
                      <div className="maps-pin-notice is-success">
                        <CheckCircle2 size={13} className="maps-pin-notice-icon" />
                        <span className="maps-pin-notice-text">{locationNotice}</span>
                      </div>
                    )}

                    {/* Warning Notice: Shown only when NO coordinates/URL exist and permission was blocked */}
                    {locationNotice && !googleMapsUrl && !locationCoords && (locationNotice.includes('blocked') || locationNotice.includes('denied') || locationNotice.includes('permission') || locationNotice.includes('unavailable') || locationNotice.includes('timed out')) && (
                      <div className="maps-pin-notice is-warning">
                        <AlertCircle size={13} className="maps-pin-notice-icon" />
                        <div className="maps-pin-notice-body">
                          <span className="maps-pin-notice-text">{locationNotice}</span>
                          <button
                            type="button"
                            onClick={handleRequestBookingLocation}
                            className="maps-retry-perm-btn"
                          >
                            Ask Again
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="client-pincode">Pincode (Optional)</label>
                    <input
                      id="client-pincode"
                      type="text"
                      placeholder="e.g. 490020"
                      className="form-input"
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="client-notes">Special Requests / Preferences</label>
                    <input
                      id="client-notes"
                      type="text"
                      placeholder="Skin sensitivities, product preferences, or beautician notes..."
                      className="form-input"
                      value={specialRequests}
                      onChange={(e) => setSpecialRequests(e.target.value)}
                    />
                  </div>

                  {/* Trust reassurance */}
                  <div className="checkout-trust-banner">
                    <ShieldCheck size={16} className="trust-shield-icon" />
                    <span>Single-use sterile disposables · Certified female beauticians · Pay after service</span>
                  </div>
                </div>

                {/* Out of Serving Area Red Banner directly above submit bar */}
                {isOutOfServiceArea && (
                  <div className="loc-out-of-area-red-alert checkout-out-of-area-strip" role="alert" style={{ marginBottom: '0.85rem' }}>
                    <span className="loc-red-dot">🔴</span>
                    <span className="loc-red-title">
                      Doorstep parlour care in your Area is Coming Soon!
                    </span>
                  </div>
                )}

                {/* ── STEP 2 SUBMIT & BACK BAR ── */}
                <div className="checkout-submit-bar-multi">
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      const container = document.querySelector('.cart-modal-container');
                      if (container) container.scrollTop = 0;
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="btn btn-outline step-back-btn"
                  >
                    <ArrowLeft size={16} />
                    <span>Back</span>
                  </button>

                  <button
                    type="submit"
                    disabled={!isPhoneValid || isSubmitting || isOutOfServiceArea}
                    className={`btn step-confirm-btn ${
                      !isPhoneValid || isSubmitting || isOutOfServiceArea
                        ? 'is-disabled-confirm'
                        : 'btn-berry'
                    }`}
                  >
                    {isOutOfServiceArea ? (
                      <span>Doorstep Service Coming Soon in Your Area</span>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        <span>{isSubmitting ? 'Booking & Assigning...' : 'Confirm Doorstep Appointment'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    );

  const renderLocationPermissionModal = () => {
    if (!showLocationPermissionModal) return null;

    return (
      <div
        className="location-permission-modal-overlay"
        onClick={(e) => {
          e.stopPropagation();
          setShowLocationPermissionModal(false);
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-perm-modal-title"
      >
        <div
          className="location-permission-modal-card"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="location-perm-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowLocationPermissionModal(false);
            }}
            aria-label="Close location permission prompt"
          >
            <X size={18} />
          </button>

          <div className="location-perm-icon-wrap">
            <div className="location-perm-icon-pulse" />
            <Navigation size={26} className="location-perm-icon" />
          </div>

          <h3 id="location-perm-modal-title" className="location-perm-title">
            Allow Live GPS Location?
          </h3>

          <p className="location-perm-desc">
            Blishka needs your location permission to capture your exact doorstep coordinates on Google Maps so your beautician reaches your home smoothly without calling for directions.
          </p>

          <div className="location-perm-perks">
            <div className="location-perm-perk">
              <CheckCircle2 size={16} className="perm-perk-icon" />
              <div>
                <strong>Direct Doorstep Navigation</strong>
                <span>Assigned beautician gets 1-tap route directly to your home or gate.</span>
              </div>
            </div>
            <div className="location-perm-perk">
              <ShieldCheck size={16} className="perm-perk-icon" />
              <div>
                <strong>100% Private &amp; Secure</strong>
                <span>Coordinates are used solely for your appointment dispatch and never shared.</span>
              </div>
            </div>
          </div>

          <div className="location-perm-actions">
            <button
              type="button"
              className="location-perm-btn-allow"
              onClick={(e) => {
                e.stopPropagation();
                handleConfirmLocationPermission();
              }}
            >
              <Navigation size={15} />
              <span>Allow &amp; Pin Live GPS</span>
            </button>
            <button
              type="button"
              className="location-perm-btn-cancel"
              onClick={(e) => {
                e.stopPropagation();
                setShowLocationPermissionModal(false);
              }}
            >
              Enter Manually / Cancel
            </button>
          </div>

          <p className="location-perm-browser-hint">
            Your browser or device may also ask <em>"Allow Blishka to access your location?"</em>. Please select <strong>Allow</strong>.
          </p>
        </div>
      </div>
    );
  };

  if (isPageView) {
    return (
      <div className="multipage-view cart-page-view">
        <div className="section-container cart-page-content-container">
          {cardContent}
        </div>
        {renderLocationPermissionModal()}
      </div>
    );
  }

  return (
    <div className="modal-overlay cart-page-overlay" onClick={onClose}>
      {cardContent}
      {renderLocationPermissionModal()}
    </div>
  );
};
